import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';
import { computeSceneGroupShortIds, resolveSceneGroupHandle } from './scene-group-addressing';
import { findWordSequenceIndices } from './transcript-word-match';
import { VideoStateTool } from './VideoStateTool';

import { applyPlaybackRegionsToSceneGroupOperation } from 'js/src/data/clip-creator/clip-creator-operations';
import { wordSpanToClipRegion } from 'js/src/data/transcript/word-timing';
import { checkpointVideo } from 'js/src/data/video-data';
import type { RootState } from 'js/src/libs/RootState';
import { saveRawLuminary } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

export const trimSceneGroupSchema = z.object({
    scene_group_id: z
        .string()
        .describe(
            'The stable scene_group_id of the voiceover or talking-head scene group to trim (from the video state).',
        ),
    words: z
        .array(z.string())
        .describe(
            'The exact consecutive transcript words of the section to KEEP, in order as spelled in the transcript. Everything outside this span is discarded and the video gets shorter. The kept section starts and ends on a word boundary.',
        ),
});
export type TrimSceneGroupArgs = z.infer<typeof trimSceneGroupSchema>;

export class TrimSceneGroupTool extends VideoStateTool<typeof trimSceneGroupSchema> {
    protected override readonly schema = trimSceneGroupSchema;
    protected override readonly toolName = TOOL_NAMES.trimSceneGroup;
    protected override readonly toolDescription = `Shorten an existing voiceover or talking-head scene group to a single section of its recording: keep the given consecutive transcript words and discard the rest, so the video gets shorter. Choose ${TOOL_NAMES.trimSceneGroup} when the user wants only a clip or moment of a recording (e.g. a section for social). (To divide the narration into two independently-editable parts while keeping all the footage, use ${TOOL_NAMES.splitSceneGroup}.)`;

    public override getToolDisplayName(): string {
        return 'Trimming scene group';
    }

    public get statusIcon(): string {
        return 'content_cut';
    }

    public async execute(args: TrimSceneGroupArgs, context: ToolExecutionContext) {
        const { words: requestedWords } = args;
        const { parentActionGroup } = context;

        if (requestedWords.length === 0) {
            return `Error: "words" was empty. Pass the transcript words of the section to keep.`;
        }

        const { sceneGroup } = resolveSceneGroupHandle(this.luminary, args);

        // Match against the group's OWN transcript, so the cut lands on the same words the agent
        // read — no timestamps to drift. originalTranscript word times reflect any user edits.
        const transcript = sceneGroup.originalTranscript;
        const transcriptWords = transcript?.transcriptData?.words || transcript?.words || [];
        if (!transcript || transcriptWords.length === 0) {
            return `Error: Scene group ${sceneGroup.id} does not have a transcript. Only voiceover and talking-head scene groups can be trimmed.`;
        }

        const match = findWordSequenceIndices(transcriptWords, requestedWords);
        if (!match) {
            return `Error: "${requestedWords.join(' ')}" does not appear in this recording's transcript, so there is no section to keep. Copy the words exactly as they appear in the transcript.`;
        }
        const keptWordSpan = wordSpanToClipRegion(transcriptWords, match);

        const { applyPlaybackRegionsToSceneGroup } = makeOperationsCallable(
            { applyPlaybackRegionsToSceneGroupOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );

        try {
            // The same cut clip_select previews and add_talking_head_scene_group's `words` apply,
            // reset to just the kept span (uncut to full, then cut everything outside it).
            applyPlaybackRegionsToSceneGroup(sceneGroup, [
                [keptWordSpan.startTimeMs, keptWordSpan.endTimeMs],
            ]);

            this.dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(this.luminary),
                    systemTriggered: false,
                    parentActionGroup,
                }),
            );

            const shortId =
                computeSceneGroupShortIds(this.luminary.sceneGroups.map((group) => group.id)).get(
                    sceneGroup.id,
                ) ?? sceneGroup.id;
            return `Success: Trimmed scene group ${shortId} to the section "${requestedWords.join(' ')}" (${Math.round(keptWordSpan.startTimeMs)}ms–${Math.round(keptWordSpan.endTimeMs)}ms of the recording); the rest of the recording was discarded.`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error trimming scene group: ${errorMessage}`;
        }
    }
}
